import React, { useState, useEffect, useMemo } from "react";
import styles from "./index.module.css";
import Button, { ButtonPurpose, ButtonSize } from "shared/Button";
import Table from "shared/Table";
import { Modal } from "shared/Modal";
import EditModalContent from "./EditModalContent";
import RemoveModalContent from "./RemoveModalContent";
import { useExternal } from "./useExternal";
import { useAwards } from "./useAwards";
import classNames from "classnames";
import PagingControls from "shared/PagingControls";
import Loader from "shared/Loader";
import formatDate from "shared/@utils/formatDate";

import nanobus from "nanobus";
import AddPlugin from "./AddExternalPlugin/AddPlugin";
import TabActionBar from "shared/TabActionBar";

export default function External(props) {
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);

  const events = useMemo(() => nanobus(), []);

  const { records, loading, isWaiting, refresh } = useExternal({
    page,
    limit,
  });

  const { awards } = useAwards();

  const [selectedIndex, setSelectedIndex] = useState(0);

  const selected = records && records.data[selectedIndex];

  useEffect(() => {
    if (loading) {
      events.emit("refresh:start");
    } else {
      events.emit("refresh:end");
    }
  }, [loading, events]);

  // Listen for refresh events on mount and stop listening when unmounted.
  useEffect(() => {
    const listener = () => {
      refresh();
    };

    events.on("refresh", listener);

    return () => {
      events.removeListener("refresh", listener);
    };
  }, [refresh, events]);

  const [showPluginModal, setShowPluginModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);

  return (
    <>
      <TabActionBar>
        <Button
          onClick={() => {
            setShowPluginModal(true);
          }}
        >
          Add Plugin
        </Button>
      </TabActionBar>

      {/* <TextInput placeholder="Search..." /> */}

      <div className={styles.tableContainer}>
        {records == null ? (
          <Loader />
        ) : (
          <>
            <Table
              className={classNames({
                [styles.loading]: loading,
              })}
            >
              <thead>
                <tr>
                  <th>Edit</th>
                  <th>IBO Number</th>
                  <th>Award</th>
                  <th>Sponsor</th>
                  <th>Platinum</th>
                  <th>Emerald</th>
                  <th>Date</th>
                  <th style={{ textAlign: "right" }}>Remove</th>
                </tr>
              </thead>
              <tbody>
                {records.data.map((r, i) => {
                  return (
                    <tr key={i}>
                      <td>
                        <Button
                          size={ButtonSize.small}
                          className={styles.editButton}
                          onClick={() => {
                            setShowEditModal(true);
                          }}
                        >
                          Edit
                        </Button>
                      </td>
                      <td>{r.iboNumber}</td>
                      {awards ? (
                        <td>{awards[r.losAwardId - 1].wwgDesc}</td>
                      ) : (
                        <td>Loading Awards...</td>
                      )}
                      <td>{r.sponsor}</td>
                      <td>{r.platinum}</td>
                      <td>{r.emerald}</td>
                      <td>{formatDate(r.dateAdded)}</td>
                      <td align="right">
                        <Button
                          size={ButtonSize.small}
                          purpose={ButtonPurpose.delete}
                          onClick={() => {
                            setShowRemoveModal(true);
                          }}
                        >
                          Remove
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            <div className={styles.pagingControls}>
              <PagingControls
                page={page}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={setLimit}
                disableNext={records.length < limit}
                disabled={loading || isWaiting}
              />
            </div>
          </>
        )}
      </div>

      {/* Add Plugin Modal */}
      <Modal
        title="New External Plugin"
        isOpen={showPluginModal}
        onClose={() => {
          setShowPluginModal(false);
        }}
      >
        <AddPlugin
          record={selected}
          refresh={() => {
            refresh();
          }}
          onClose={() => {
            setShowPluginModal(false);
            refresh();
          }}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        title="Edit External Plugin"
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
        }}
      >
        <EditModalContent
          record={selected}
          refresh={refresh}
          onClose={() => {
            setShowEditModal(false);
          }}
        />
      </Modal>
      {/* Remove Modal */}
      {selected && (
        <Modal
          title={"Delete Plugin Ibo " + selected.iboNumber + "?"}
          isOpen={showRemoveModal}
          onClose={() => {
            setShowRemoveModal(false);
          }}
        >
          <RemoveModalContent
            record={selected}
            onClose={() => {
              setShowRemoveModal(false);
              refresh();
            }}
          />
        </Modal>
      )}
    </>
  );
}
